import React, { useEffect, useMemo } from 'react';
import tenantUtils from '@utils';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../../components/common';
import { formatNumberString } from '../../../../utility/utility';
import {
  adLicenseRegaDetailModalOpenEvent,
  adLicenseClickEvent,
} from '../../../../services/analyticsService';
import { PAGE_SECTIONS } from '../../../../services/analyticsService/constants';
import { useGetRegaAdLicenseDetailQuery } from '../../apis/regaAdLicenses';
import RegaDetailSections from '../post-listing/RegaDetailSections';
import RegaDetailBand from '../post-listing/RegaDetailBand';
import RegaDetailFooter from './rega-detail-footer';
import { getLicenseTitle, getLicenseLocation, formatDetailDate } from './utils';
import { ChevronLeftIcon, CloseIcon } from './icons';
import { SkeletonBlock } from './styled';
import { v2Color } from '@theme/v2';

const headerBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: 0,
  background: 'transparent',
  border: 0,
  color: v2Color('#006169', 'text'),
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const yesNo = (t, value) => (value == null ? '-' : value ? t('Yes') : t('No'));

const RegaAdLicenseDetail = ({ id, onBack, onClose, onPostLicense, hideBack = false }) => {
  const { t } = useTranslation();
  const tc = (key) => t(key, { context: 'adLicense' });
  const { user } = useSelector((state) => state.app.loginUser);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const { data: detail, isFetching, isError, refetch } = useGetRegaAdLicenseDetailQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const licenseNo = detail?.ad_license_number;

  useEffect(() => {
    if (licenseNo) adLicenseRegaDetailModalOpenEvent(user, licenseNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseNo]);

  const cityCrumb = (detail?.location?.breadcrumbs || []).find((crumb) => crumb?.level === 2);

  const sections = useMemo(() => {
    if (!detail) return [];
    const info = detail.license_info || {};
    const responsible = detail.responsible_info || {};
    const additional = detail.additional_info || {};
    const utilities = additional.utilities || {};
    return [
      {
        label: tc('License Info'),
        keys: [
          { label: t('FAL License no.'), value: info.fal_license_number },
          { label: tc('Created date'), value: formatDetailDate(info.created_date) },
          { label: t('Expiry Date'), value: formatDetailDate(info.expiry_date) },
        ],
      },
      {
        label: tc('Ad Responsible Info'),
        keys: [
          { label: t('Responsible Name'), value: responsible.responsible_name },
          { label: t('Responsible Number'), value: responsible.responsible_number },
        ],
      },
      {
        label: tc('Location'),
        keys: [
          // REGA has no region in the contract — renders "—" until BE adds it.
          { label: t('Region'), value: '-' },
          { label: t('City'), value: cityCrumb ? tenantUtils.getLocalisedString(cityCrumb, 'title') : '' },
          { label: t('District'), value: tenantUtils.getLocalisedString(detail.location, 'title') },
        ],
      },
      {
        label: tc('Property Info'),
        keys: [
          { label: t('Ad type'), value: tenantUtils.getLocalisedString(detail.ad_type, 'title') },
          { label: t('Property usage'), value: (additional.property_usage || []).join(', ') },
          { label: t('Property Type'), value: tenantUtils.getLocalisedString(detail.property_type, 'title') },
          {
            label: t('Area size'),
            value: detail.area_unit_value != null ? `${detail.area_unit_value} ${t('Sq. M.')}` : '',
          },
          { label: t('No. of rooms'), value: additional.number_of_rooms },
        ],
      },
      {
        label: tc('Utilities'),
        keys: [
          { label: t('Electricity'), value: yesNo(t, utilities.electricity) },
          { label: tc('Sanitation'), value: yesNo(t, utilities.sanitation) },
          { label: tc('Water'), value: yesNo(t, utilities.water) },
        ],
      },
      {
        label: tc('Additional Info'),
        keys: [
          { label: t('Property age'), value: additional.property_age },
          { label: t('Street width'), value: additional.street_width },
          { label: t('Plan no'), value: additional.plan_number },
          { label: tc('Deed no.'), value: additional.deed_number },
          { label: t('Property Facade'), value: additional.property_face },
          { label: tc('Border & lengths'), value: additional.borders_and_lengths },
          { label: tc('Guarantees or Duration'), value: additional.guarantees_and_duration },
          { label: t('Channels'), value: (additional.channels || []).join(', ') },
          { label: tc('Any obligations on property'), value: additional.obligations_on_property },
          {
            label: tc('Compliance with Saudi building'),
            value: yesNo(t, additional.compliance_with_saudi_building_code),
          },
          { label: t('Property is pawned'), value: yesNo(t, additional.is_pawned) },
          { label: tc('Property is Constrained'), value: yesNo(t, additional.is_constrained) },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '16px 24px',
        borderBlockEnd: '1px solid #f0f0f0',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {!hideBack && onBack && (
          <>
            <button type="button" onClick={onBack} style={headerBtn}>
              <ChevronLeftIcon size={16} />
              <span>{t('Back')}</span>
            </button>
            <span style={{ width: 1, height: 18, background: 'rgba(5,5,5,0.10)' }} />
          </>
        )}
        <span style={{ color: 'rgba(0,0,0,0.88)', fontSize: 16, fontWeight: 700, lineHeight: 1.5 }}>
          {tc('Listing Details from REGA')}
        </span>
      </span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Close')}
        style={{
          display: 'inline-grid',
          placeContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: 'transparent',
          border: 0,
          borderRadius: 6,
          color: 'rgb(112,112,112)',
          cursor: 'pointer',
        }}
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );

  let body;
  if (isFetching) {
    body = (
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gap: 16, padding: 24, alignContent: 'start' }}>
        <SkeletonBlock height="90px" />
        <SkeletonBlock height="44px" />
        <SkeletonBlock height="120px" />
      </div>
    );
  } else if (isError || !detail) {
    body = (
      <div style={{ flex: 1, minHeight: 0, padding: 24 }}>
        <EmptyState type="analytics-dashboard" message={t('Something went wrong')} onClick={refetch} />
      </div>
    );
  } else {
    // Detail payload exposes the REGA deep-link as `rega_url` (PR #14097 contract); it is null
    // until the mirror has a url for the record, in which case the QR + link stay hidden.
    const regaUrl = detail.rega_url;
    body = (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <RegaDetailBand
          typeLabel={getLicenseTitle(detail.property_type, detail.purpose, t)}
          price={formatNumberString(detail.price)}
          adLicense={detail.ad_license_number}
          location={getLicenseLocation(detail.location)}
          area={detail.area_unit_value}
          url={regaUrl}
          onRegaClick={() =>
            adLicenseClickEvent(user, { pageSection: PAGE_SECTIONS.REGA_LINK, value: detail.ad_license_number })
          }
        />

        <div style={{ paddingBlock: 16 }}>
          <RegaDetailSections sections={sections} />
        </div>
      </div>
    );
  }

  return (
    // Fixed height (not maxHeight) so the detail doesn't grow/re-centre as it loads — and it matches
    // the list view's height, so switching list -> detail on desktop has no jerk. Body scrolls.
    <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 60px)' : '82vh' }}>
      {header}
      {body}
      <RegaDetailFooter licenseNo={licenseNo} onPostLicense={onPostLicense} isMobile={isMobile} />
    </div>
  );
};

export default RegaAdLicenseDetail;
